import { FunctionComponent, useState } from 'react'
import { map } from 'rxjs'
import fcObserveRequest from 'helpers/fcObserveRequest'
import useObservable from 'libraries/front-end-core/hooks/useObservable'
import ResponseConsumer from 'components/shared/ResponseConsumer'
import {
  RESPONSE_PENDING,
  RESPONSE_FORBIDDEN
} from 'libraries/front-end-core/constants/responses'
import {
  URL_PARTNER_CONSENT_DETAILS,
  URL_PARTNER_CONSENT_DECISION
} from 'constants/urls'

const SCOPE_LABELS: Record<string, string> = {
  'knowledge:read': 'Read FromCounsel knowledge content on your behalf.'
}

interface ConsentDetails {
  clientId: string
  clientName: string
  scopes: string[]
}

interface DecisionState {
  submitting: boolean
  error: string | null
}

interface Props {
  consentChallenge: string
}

const noop = { subscribe: () => ({ unsubscribe: () => {} }) }

const Main: FunctionComponent<Props> = ({ consentChallenge }) => {
  const [decisionState, setDecisionState] = useState<DecisionState>({
    submitting: false,
    error: null
  })
  const [detailsObs] = useState<any>(() => {
    if (consentChallenge === '') return noop
    return fcObserveRequest({
      url: `${URL_PARTNER_CONSENT_DETAILS}?consent_challenge=${encodeURIComponent(consentChallenge)}`
    }).pipe(
      map((r: unknown) => (typeof r === 'string' ? JSON.parse(r) : r))
    )
  })

  const detailsResponse = useObservable(detailsObs, RESPONSE_PENDING)

  if (consentChallenge === '') {
    return (
      <div className='flex justify-center bg-ghost-white pt10 ph7 ph5-m ph7 ph5-m'>
        <div className='bg-white br3 shadow-black-16 pa8 tc max-w15 w-100'>
          <p className='f4 lh-copy black'>No consent request found. Please return to the application and try again.</p>
        </div>
      </div>
    )
  }

  const handleDecision = (action: 'accept' | 'deny'): void => {
    if (decisionState.submitting) return
    setDecisionState({ submitting: true, error: null })
    fcObserveRequest({
      url: `${URL_PARTNER_CONSENT_DECISION}?consent_challenge=${encodeURIComponent(consentChallenge)}&action=${action}`,
      method: 'POST'
    }).subscribe((result: unknown) => {
      if (result === RESPONSE_FORBIDDEN) {
        setDecisionState({ submitting: false, error: 'This request could not be completed. Please contact support.' })
      } else if (typeof result !== 'string') {
        setDecisionState({ submitting: false, error: 'Something went wrong. Please try again.' })
      } else {
        const { redirect_to: redirectTo } = JSON.parse(result) as { redirect_to: string }
        window.location.href = redirectTo
      }
    })
  }

  return (
    <div className='flex justify-center bg-ghost-white pt10 ph7 ph5-m'>
      <ResponseConsumer
        response={detailsResponse}
        notfoundComponent={() => (
          <div className='bg-white br3 shadow-black-16 pa8 tc max-w15 w-100'>
            <p className='f4 lh-copy black'>
              This request is no longer valid. Please return to the application and start again.
            </p>
          </div>
        )}
        content={() => {
          const { clientName, scopes } = detailsResponse as ConsentDetails
          return (
            <div className='bg-white br3 shadow-black-16 pa8 max-w15 w-100'>
              <h1 className='f3 fw6 lh-title black mb4'>
                {clientName} wants to access your FromCounsel account
              </h1>
              <p className='f5 lh-copy dark-gray mb4'>
                This app will access FromCounsel content on your behalf. It cannot see your account details.
              </p>
              <ul className='pl4 mb6'>
                {scopes.map(scope => (
                  <li key={scope} className='f5 lh-copy black mv2'>
                    {SCOPE_LABELS[scope] ?? scope}
                  </li>
                ))}
              </ul>
              {decisionState.error !== null && (
                <p className='f5 lh-copy indian-red mb4'>{decisionState.error}</p>
              )}
              <div className='flex justify-end gap4'>
                <button
                  type='button'
                  onClick={() => handleDecision('deny')}
                  disabled={decisionState.submitting}
                  className='pointer bg-transparent bn f5 black ph5 pv4 br3 hover-bg-ghost-white'
                >
                  Deny
                </button>
                <button
                  type='button'
                  onClick={() => handleDecision('accept')}
                  disabled={decisionState.submitting}
                  className='pointer bg-suez-canal br3 ph5 pv4 white f5 bn hover-o-90'
                >
                  {decisionState.submitting ? 'Please wait…' : 'Allow'}
                </button>
              </div>
            </div>
          )
        }}
      />
    </div>
  )
}

export default Main
